import * as i0 from '@angular/core';
import { EventEmitter, Component, ChangeDetectionStrategy, Input, ContentChild, Output } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from 'primeng/dataview';
import { DataViewModule } from 'primeng/dataview';
import { LazyDataPaging } from '@arematics/core/table_paging';
import * as i4 from '@arematics/core/switch';
import { SwitchModule } from '@arematics/core/switch';
import * as i3 from 'primeng/api';

class DataViewPagingComponent extends LazyDataPaging {
    ref;
    query;
    mapper = (input) => input;
    rowsPerPageOptions = [6, 9, 12];
    listItem;
    gridItem;
    subHeader;
    defaultLayout = 'grid';
    optionsEnabled = false;
    options;
    column = 'col-12 md:col-4';
    optionsChange = new EventEmitter();
    constructor(ref) {
        super(ref);
        this.ref = ref;
    }
    refreshOptions() {
        this.optionsChange.emit(this.options);
        this.refresh();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DataViewPagingComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: DataViewPagingComponent, isStandalone: true, selector: "a-dataview[query]", inputs: { query: "query", mapper: "mapper", rowsPerPageOptions: "rowsPerPageOptions", defaultLayout: "defaultLayout", optionsEnabled: "optionsEnabled", options: "options", column: "column" }, outputs: { optionsChange: "optionsChange" }, host: { attributes: { "ngSkipHydration": "true" } }, queries: [{ propertyName: "listItem", first: true, predicate: ["listItem"], descendants: true }, { propertyName: "gridItem", first: true, predicate: ["gridItem"], descendants: true }, { propertyName: "subHeader", first: true, predicate: ["subHeader"], descendants: true }], usesInheritance: true, ngImport: i0, template: `
		<p-dataView
			[value]="data.getValue()"
			[paginator]="true"
			[rows]="rowsPerPageOptions[0]"
			[rowsPerPageOptions]="rowsPerPageOptions"
			[lazy]="true"
			styleClass="guttered"
			[totalRecords]="totalRecords"
			(onLazyLoad)="loadData($event)"
			[loading]="loading"
			[layout]="defaultLayout">
			<ng-template pTemplate="header">
				<div class="grid align-items-center justify-content-center md:justify-content-between">
					<ng-content></ng-content>
					<p-dataViewLayoutOptions
						*ngIf="optionsEnabled"
						styleClass="flex-row ml-2"></p-dataViewLayoutOptions>
				</div>
				<div>
					<div class="flex flex-wrap justify-content-center align-items-center">
						<div
							*ngIf="options && options.switches && options.switches.length >= 1"
							class="flex flex-wrap justify-content-center align-items-center">
							<div *ngFor="let item of options.switches" class="mr-3">
								<a-switch
									[(checked)]="item.checked"
									[text]="item.title"
									(checkedChange)="refreshOptions()"></a-switch>
							</div>
						</div>
						<ng-template [ngTemplateOutlet]="subHeader"></ng-template>
					</div>
				</div>
			</ng-template>
			<ng-template let-item pTemplate="listItem" *ngIf="listItem">
				<div class="w-full">
					<ng-template
						[ngTemplateOutlet]="listItem"
						[ngTemplateOutletContext]="{ $implicit: item }"></ng-template>
				</div>
			</ng-template>
			<ng-template let-item pTemplate="gridItem" *ngIf="gridItem">
				<div class="{{ column }}">
					<ng-template
						[ngTemplateOutlet]="gridItem"
						[ngTemplateOutletContext]="{ $implicit: item }"></ng-template>
				</div>
			</ng-template>
		</p-dataView>
	`, isInline: true, dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "ngmodule", type: DataViewModule }, { kind: "component", type: i2.DataView, selector: "p-dataView", inputs: ["paginator", "rows", "totalRecords", "pageLinks", "rowsPerPageOptions", "paginatorPosition", "paginatorStyleClass", "alwaysShowPaginator", "paginatorDropdownAppendTo", "paginatorDropdownScrollHeight", "currentPageReportTemplate", "showCurrentPageReport", "showJumpToPageDropdown", "showFirstLastIcon", "showPageLinks", "lazy", "lazyLoadOnInit", "emptyMessage", "style", "styleClass", "gridStyleClass", "trackBy", "filterBy", "filterLocale", "loading", "loadingIcon", "first", "sortField", "sortOrder", "value", "layout"], outputs: ["onLazyLoad", "onPage", "onSort", "onChangeLayout"] }, { kind: "directive", type: i3.PrimeTemplate, selector: "[pTemplate]", inputs: ["type", "pTemplate"] }, { kind: "component", type: i2.DataViewLayoutOptions, selector: "p-dataViewLayoutOptions", inputs: ["style", "styleClass"] }, { kind: "ngmodule", type: SwitchModule }, { kind: "component", type: i4.SwitchComponent, selector: "a-switch[checked]", inputs: ["checked", "text"], outputs: ["checkedChange"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DataViewPagingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'a-dataview[query]', standalone: true, imports: [CommonModule, DataViewModule, SwitchModule], host: { ngSkipHydration: 'true' }, template: `
		<p-dataView
			[value]="data.getValue()"
			[paginator]="true"
			[rows]="rowsPerPageOptions[0]"
			[rowsPerPageOptions]="rowsPerPageOptions"
			[lazy]="true"
			styleClass="guttered"
			[totalRecords]="totalRecords"
			(onLazyLoad)="loadData($event)"
			[loading]="loading"
			[layout]="defaultLayout">
			<ng-template pTemplate="header">
				<div class="grid align-items-center justify-content-center md:justify-content-between">
					<ng-content></ng-content>
					<p-dataViewLayoutOptions
						*ngIf="optionsEnabled"
						styleClass="flex-row ml-2"></p-dataViewLayoutOptions>
				</div>
				<div>
					<div class="flex flex-wrap justify-content-center align-items-center">
						<div
							*ngIf="options && options.switches && options.switches.length >= 1"
							class="flex flex-wrap justify-content-center align-items-center">
							<div *ngFor="let item of options.switches" class="mr-3">
								<a-switch
									[(checked)]="item.checked"
									[text]="item.title"
									(checkedChange)="refreshOptions()"></a-switch>
							</div>
						</div>
						<ng-template [ngTemplateOutlet]="subHeader"></ng-template>
					</div>
				</div>
			</ng-template>
			<ng-template let-item pTemplate="listItem" *ngIf="listItem">
				<div class="w-full">
					<ng-template
						[ngTemplateOutlet]="listItem"
						[ngTemplateOutletContext]="{ $implicit: item }"></ng-template>
				</div>
			</ng-template>
			<ng-template let-item pTemplate="gridItem" *ngIf="gridItem">
				<div class="{{ column }}">
					<ng-template
						[ngTemplateOutlet]="gridItem"
						[ngTemplateOutletContext]="{ $implicit: item }"></ng-template>
				</div>
			</ng-template>
		</p-dataView>
	`, changeDetection: ChangeDetectionStrategy.OnPush }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { query: [{
                type: Input
            }], mapper: [{
                type: Input
            }], rowsPerPageOptions: [{
                type: Input
            }], listItem: [{
                type: ContentChild,
                args: ['listItem']
            }], gridItem: [{
                type: ContentChild,
                args: ['gridItem']
            }], subHeader: [{
                type: ContentChild,
                args: ['subHeader']
            }], defaultLayout: [{
                type: Input
            }], optionsEnabled: [{
                type: Input
            }], options: [{
                type: Input
            }], column: [{
                type: Input
            }], optionsChange: [{
                type: Output
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { DataViewPagingComponent };
